<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import ProgressSpinner from 'primevue/progressspinner'
import CopyButton from '@/components/CopyButton.vue'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import GbButton from '@/components/GbButton.vue'
import ShareLinkInvalidMessage from '@/components/ShareLinkInvalidMessage.vue'
import SharedContentBanner from '@/components/SharedContentBanner.vue'
import TermsOfUseFooter from '@/components/TermsOfUseFooter.vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import { useCopyButtonPermission } from '@/components/use_copy_button_permission'
import { useUserFeedbackContext } from '@/components/useUserFeedbackContext'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { setTitle } from '@/util/title'
import { VUE2_CONTAINER_WIDTHS } from '@/util/vue2_layout'
import { getSharedStudentStatement, type SharedStudentStatement } from './shared_statement_api'
import { SHARED_STATEMENT_EVENT_NAMES } from './shared_statement_event_names'

// Unlike Vue 2, the tab title and the feedback form leave out the student's name: neither is masked.
const SHARED_STATEMENT_TITLE = 'Shared Present Levels Statement'

const props = defineProps<{ uuid: string }>()

// Vue 2's Bootstrap .btn box (128x34): 6px 12px padding on a 20px line inside a 1px border.
const COPY_STATEMENT_CLASSES = '!px-3 !py-1.5 !leading-5 border border-transparent'

const router = useRouter()
const { isCopyBlocked, reportBlockedCopy } = useCopyButtonPermission()
const { updateContext: updateFeedbackContext } = useUserFeedbackContext()

const isLoading = ref(true)
const sharedStatement = ref<SharedStudentStatement | null>(null)

const student = computed(() => sharedStatement.value?.student)
const ownerName = computed(() => sharedStatement.value?.user?.name ?? '')
const draftedStatement = computed(() => student.value?.drafted_strengths ?? '')
/** Vue 2 treats a statement with nothing drafted the same as a link that failed to load. */
const isLinkInvalid = computed(() => !isLoading.value && !draftedStatement.value)

const analytics = getAnalyticsTracker().getChildContext({
  plw_uuid: computed(() => props.uuid),
  plw_enrolled_grade: computed(() => student.value?.enrolled_grade_level ?? ''),
  plw_areas_of_need: computed(() => student.value?.selected_subjects ?? []),
  plw_is_library: true,
  student_uuid: computed(() => student.value?.student_uuid ?? '')
})

const shareLink = computed(
  () => window.location.origin + router.resolve({ name: 'SharedPresentLevelsPage', params: { uuid: props.uuid } }).href
)

function showSharedStatement(loaded: SharedStudentStatement) {
  sharedStatement.value = loaded
  setTitle(SHARED_STATEMENT_TITLE)
  updateFeedbackContext({ uuid: props.uuid, title: SHARED_STATEMENT_TITLE })
  analytics.sendEvent(SHARED_STATEMENT_EVENT_NAMES.VIEWED)
}

function recordStatementCopied() {
  analytics.sendEvent(SHARED_STATEMENT_EVENT_NAMES.CLICKED_COPY_STATEMENT)
}

let latestFetch = 0

async function fetchSharedStatement() {
  const thisFetch = ++latestFetch
  isLoading.value = true
  sharedStatement.value = null
  try {
    const loaded = await getSharedStudentStatement(props.uuid)
    if (thisFetch !== latestFetch) return
    showSharedStatement(loaded)
  } catch (error) {
    if (thisFetch !== latestFetch) return
    console.error(error)
  } finally {
    if (thisFetch === latestFetch) isLoading.value = false
  }
}

watch(() => props.uuid, fetchSharedStatement, { immediate: true })
</script>

<template>
  <div>
    <div v-if="isLoading" class="container mt-6 flex items-center text-text-xl text-gray-700" :class="VUE2_CONTAINER_WIDTHS" data-testid="shared-statement-loading">
      <ProgressSpinner stroke-width="8" class="!m-0 !h-5 !w-5 stroke-primary-400" aria-hidden="true" />
      <span class="ml-2">Loading...</span>
    </div>
    <ShareLinkInvalidMessage v-else-if="isLinkInvalid" class="mt-6">
      This link is either invalid, or the present levels statement is no longer shared by its owner. Ask the owner of the present levels statement for a new shareable link.
    </ShareLinkInvalidMessage>
    <CopyTrackWrapper v-else-if="student" :type="CONTENT_TYPES.PRESENTLEVEL" :uuid="uuid">
      <SharedContentBanner
        v-if="student.student_name"
        content-type="a present levels statement"
        :owner-name="ownerName"
        :last-updated="student.updated_at"
        :share-link="shareLink"
        :share-content-type="CONTENT_TYPES.PRESENTLEVEL"
        :container-class="VUE2_CONTAINER_WIDTHS"
        auto-hide-copy-tooltip
      />
      <div class="container pt-5" :class="VUE2_CONTAINER_WIDTHS">
        <div class="lg:grid lg:grid-cols-12 lg:gap-x-[30px]">
          <div class="lg:col-span-9 xl:col-span-8">
            <h1 class="text-display-lg font-medium leading-10 text-gray-900">
              Present levels drafted for <span class="fs-exclude italic text-objective-blueAction [overflow-wrap:anywhere]">{{ student.student_name }}</span>
            </h1>
            <div class="mt-8 rounded-xxs bg-objective-blueLight p-4 shadow" data-testid="statement-card">
              <div class="flex justify-end p-2">
                <GbButton
                  v-if="isCopyBlocked"
                  type="button"
                  aria-disabled="true"
                  gb-theme="greenPrimary"
                  size="small"
                  class="!pointer-events-auto"
                  :class="COPY_STATEMENT_CLASSES"
                  @click="reportBlockedCopy"
                >Copy Statement</GbButton>
                <CopyButton
                  v-else
                  :string-to-copy="draftedStatement"
                  button-text="Copy Statement"
                  button-theme="greenPrimary"
                  size="small"
                  :custom-class="COPY_STATEMENT_CLASSES"
                  :is-icon-shown="false"
                  tooltip-text="Present levels statement copied"
                  auto-hide-tooltip
                  show-copied-check
                  @copied="recordStatementCopied"
                />
              </div>
              <WikiText class="fs-exclude text-text-2xl leading-8 text-gray-900" :text="draftedStatement" />
            </div>
          </div>
        </div>
        <TermsOfUseFooter />
      </div>
    </CopyTrackWrapper>
  </div>
</template>
